/**
 * Pure helpers for the Filters panel, its chips and saved filters (rev6 Part 4). Values live in
 * the /tasks URL as comma lists ("language=kor,hin"), which the server reads as "any of".
 */
import { CONTENT_TYPE_LABELS, shortName } from './taskUi';

// The panel's fields, in the order it shows them
export const LIST_FIELDS = ['language', 'stage', 'category', 'contentType', 'platform', 'status', 'assignee', 'pm'];
export const DATE_FIELDS = ['posting', 'due'];
export const FILTER_FIELDS = [...LIST_FIELDS, ...DATE_FIELDS];

export const FIELD_LABELS = {
    language: 'Language',
    stage: 'Stage',
    category: 'Category',
    contentType: 'Type',
    platform: 'Platform',
    status: 'Status',
    assignee: 'Assignee',
    pm: 'PM',
    posting: 'Posting date',
    due: 'Stage due',
};

// Shorter names for the chips: "Posting: next 2 weeks"
const CHIP_LABELS = { ...FIELD_LABELS, posting: 'Posting', due: 'Due' };

/** The same words everywhere. This week is Monday to Sunday; Next 2 weeks is today and 13 more days. */
export const DATE_PRESETS = [
    { value: 'overdue', label: 'Overdue' },
    { value: 'week', label: 'This week' },
    { value: 'next2w', label: 'Next 2 weeks' },
    { value: 'month', label: 'This month' },
];

// Posting date also has No date; the bank and the trays are built on it
export const POSTING_PRESETS = [...DATE_PRESETS, { value: 'none', label: 'No date' }];
export const datePresets = (field) => (field === 'posting' ? POSTING_PRESETS : DATE_PRESETS);

export const STATUS_OPTIONS = [
    { key: 'ASSIGNED', label: 'To do' },
    { key: 'IN_PROGRESS', label: 'In progress' },
    { key: 'BLOCKED', label: 'Blocked' },
    { key: 'ON_HOLD', label: 'On hold' },
    { key: 'WAITING', label: 'Waiting' },
    { key: 'READY_TO_START', label: 'Video ready to start' },
];

export const TYPE_OPTIONS = Object.entries(CONTENT_TYPE_LABELS).map(([key, label]) => ({ key, label }));

/** Fields the panel shows on this view: the view's own filters; My tasks has no Assignee. */
export const panelFields = (view) => {
    const allowed = view?.schema?.filters || [];
    return FILTER_FIELDS.filter((f) => allowed.includes(f) && !(view?.viewKey === 'mine' && f === 'assignee'));
};

export const listValues = (value) =>
    value === undefined || value === null || value === ''
        ? []
        : String(value)
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean);

/** Turns one value on or off in a comma list; undefined when none is left. */
export const toggleValue = (current, value) => {
    const values = listValues(current);
    const next = values.includes(value) ? values.filter((v) => v !== value) : [...values, value];
    return next.length ? next.join(',') : undefined;
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const shortDate = (iso) => {
    const [, m, d] = iso.split('-').map(Number);
    return `${d} ${MONTHS[m - 1]}`;
};

/** "next 2 weeks", "1 Nov to 15 Nov", "from 1 Nov", "until 15 Nov". */
export const dateLabel = (value) => {
    const preset = POSTING_PRESETS.find((p) => p.value === value);
    if (preset) return preset.label.toLowerCase();
    const [from, to] = String(value).split('..');
    if (from && to) return `${shortDate(from)} to ${shortDate(to)}`;
    if (from) return `from ${shortDate(from)}`;
    if (to) return `until ${shortDate(to)}`;
    return value;
};

/** "2026-11-01..2026-11-15" from the custom range boxes; undefined when both are empty. */
export const rangeValue = (from, to) => (from || to ? `${from || ''}..${to || ''}` : undefined);

export const parseRange = (value) => {
    if (!value || !String(value).includes('..')) return { from: '', to: '' };
    const [from, to] = String(value).split('..');
    return { from: from || '', to: to || '' };
};

/**
 * What a value is called on a chip. lookups: { language: {kor: 'Korean'}, stage: {...},
 * category: {...}, platform: {...} }; people show as short names.
 */
export const valueLabel = (field, value, lookups = {}) => {
    if (field === 'contentType') return CONTENT_TYPE_LABELS[value] || value;
    if (field === 'status') return STATUS_OPTIONS.find((s) => s.key === value)?.label || value;
    if (field === 'assignee' || field === 'pm') return shortName(value);
    return lookups[field]?.[value] || value;
};

/** One chip per value ("Language: Korean"), and one per date field. */
export const filterChips = (state, fields, lookups) => {
    const chips = [];
    fields.forEach((field) => {
        if (DATE_FIELDS.includes(field)) {
            if (state[field]) chips.push({ field, value: state[field], label: `${CHIP_LABELS[field]}: ${dateLabel(state[field])}` });
            return;
        }
        listValues(state[field]).forEach((value) =>
            chips.push({ field, value, label: `${CHIP_LABELS[field]}: ${valueLabel(field, value, lookups)}` }),
        );
    });
    return chips;
};

/** The URL change that removes one chip. */
export const removeChip = (state, chip) => ({
    [chip.field]: DATE_FIELDS.includes(chip.field) ? undefined : toggleValue(state[chip.field], chip.value),
    page: 0,
});

export const clearAllPatch = (fields) => Object.fromEntries([...fields.map((f) => [f, undefined]), ['page', 0]]);

// ------------------------------------------------------------------ saved filters

// What a saved filter keeps besides the view's filters (the server keeps the same list)
export const SAVED_SETTINGS = ['group', 'sort', 'direction', 'layout', 'on', 'allPms', 'upcoming'];

/** A saved filter's query string as URL state. */
export const queryState = (query) => {
    const params = new URLSearchParams(query || '');
    const state = {};
    params.forEach((v, k) => {
        if (v === '') return;
        state[k] = state[k] ? `${state[k]},${v}` : v;
    });
    if (state.allPms !== undefined) state.allPms = state.allPms === 'true';
    if (state.upcoming !== undefined) state.upcoming = state.upcoming === 'true';
    return state;
};

const normalized = (state, keys) => {
    const out = {};
    keys.forEach((k) => {
        let v = state[k];
        if (k === 'layout' && v === 'list') v = undefined;
        if (v === undefined || v === null || v === '' || v === false) return;
        out[k] = LIST_FIELDS.includes(k) ? listValues(v).sort().join(',') : String(v);
    });
    return out;
};

/** Whether the screen still matches the saved filter; otherwise its rail name says "edited". */
export const matchesSaved = (state, saved, filterKeys) => {
    const keys = [...filterKeys, ...SAVED_SETTINGS];
    const a = normalized(state, keys);
    const b = normalized(queryState(saved?.query), keys);
    const ka = Object.keys(a).sort();
    const kb = Object.keys(b).sort();
    return ka.length === kb.length && ka.every((k, i) => k === kb[i] && a[k] === b[k]);
};

/** URL state that opens a saved filter, replacing whatever was on screen. */
export const openSavedState = (saved) => ({
    view: saved.viewKey,
    saved: saved.id,
    ...queryState(saved.query),
    page: 0,
});

/** The saved filters' rail entries for a view rail: mine, then team ones. */
export const savedSections = (filters) => ({
    mine: (filters || []).filter((f) => f.mine),
    team: (filters || []).filter((f) => !f.mine),
});
